import React, { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { GoogleSignInButton, GoogleSignInProps } from '@zr/common/packages/zr-components/Google/SignIn/GoogleSignInButton';

import {
  STRING_FIELD_EMAIL_ADDRESS_REQUIRED,
  STRING_FIELD_EMAIL_ADDRESS_INVALID,
} from '@zr/components/Fields/labels';

import EmailAddressField from '@zr/components/Fields/Formik/EmailAddressField';
import candidateCreateFromSearch from '@zr/utilities/api/candidateCreateFromSearch';

import './CollectEmailAddress.less';

// The text to be displayed in the Collect Email Address component
const strings = {
  EMAIL_COLLECTION: 'What email should the hiring manager contact you at?',
  CREATING_ACCOUNT: 'By creating an account, I agree to the ZipRecruiter ',
  TERMS_OF_USE: 'Terms of Use',
  ACKNOWLEDGE: ' and acknowledge I have read the ',
  PRIVACY_POLICY: 'Privacy Policy',
  AGREE_TO_RECEIVE_EMAIL_ALERTS: ', and agree to receive email job alerts.',
  APPLY_NOW: 'Apply Now',
};

interface ApplyFormProps {
  encryptedQuizId: string;
  googleSignInProps: GoogleSignInProps;
  isLoading: boolean;
  method: string;
  onContinue: () => void;
  onError: (error: unknown | Record<string, unknown>) => void;
  onExistingEmail: () => void;
  setIsLoading: (arg0: boolean) => void;
}

const EMAIL_ADDRESS = 'email_address';

interface FormValues {
  [EMAIL_ADDRESS]: string;
}

// TODO: Refactor this component using a HOC or Render Props or just
// rearchitect to pull the business logic out of the form that
// collects a user's email address in the Apply Flow and on the Go
// Job Page. These components do exactly the same thing with a
// different visual presentation of the results.
export function ApplyForm({
  encryptedQuizId,
  googleSignInProps,
  isLoading,
  method,
  onContinue,
  onError,
  onExistingEmail,
  setIsLoading,
}: ApplyFormProps): JSX.Element {
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });

  // Set up validation for the email field in the form
  const validationSchema = Yup.object({
    [EMAIL_ADDRESS]: Yup.string().email(STRING_FIELD_EMAIL_ADDRESS_INVALID)
      .trim().required(STRING_FIELD_EMAIL_ADDRESS_REQUIRED),
  });

  // Set up the initial values for the form
  const initialValues: FormValues = { [EMAIL_ADDRESS]: '' };

  // Collects the JobSeeker's email and creates a JobSeeker account
  const handleFormSubmit = async (values: FormValues, { setStatus }: FormikHelpers<FormValues>) => {
    const emailAddress = values[EMAIL_ADDRESS];

    // `setIsLoading(true)` so that we can disable the close button while the
    // screen is processing/loading
    setIsLoading(true);

    try {
      // POST the data to /candidate/create-from-search
      // If there's an error, `candidateCreateFromSearch`
      // will throw an error and we'll catch it below
      const result = await candidateCreateFromSearch({
        emailAddress,
        encryptedQuizId,
        method,
      });

      // We need to check the POST response status because the `/candidate/create-from-search`
      // endpoint returns a 200 status code even if there's an error
      if (result.status === 'success') {
        // Successfully created a JobSeeker
        // Set form status so we can disable the input fields while
        // we wait for the parent component to handle the submit
        setStatus({ success: true });
        onContinue();
      } else if (result.error_type && result.error_type === 'email_exists') {
        setStatus({ success: false });
        setIsLoading(false);
        if (result.redirect_url && result.si_password_type && result.si_password_type === 'user_generated') {
          // If they have a user generated password, we should redirect them to login
          const redirectUrl = result.redirect_url;
          window.location.assign(redirectUrl);
        } else if (result.si_password_type && result.si_password_type === 'system_generated') {
          // This means that the Job Seeker has created an account with us, but they don't have a pw set
          // This can happen when a Job Seeker enters their email address into a reg form
          // When this happens, we should close the Apply Flow modal and display a new modal to them
          // to let them know that they should set a password [JS-10903]
          onExistingEmail();
        }
      } else {
        // We've got a problem that the backend is letting us know about.
        // Could be that we already have a JobSeeker with the same email
        // address or could be that the person was able to get around the
        // frontend email validation
        setStatusMessage({ type: result.status, message: result.message });
        setStatus({ success: false });
        setIsLoading(false);
      }
    } catch (error) {
      onError(error);
    }
  };

  return (
    <section className="CollectEmailAddress">
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={validationSchema}
      >
        {(formikProps) => {
          const {
            handleSubmit, isSubmitting,
          } = formikProps;

          return (
            <form className="ScreenViewWrapper CollectNameAndPhoneNumber" onSubmit={handleSubmit}>
              <h2 className="title">{strings.EMAIL_COLLECTION}</h2>

              <div className="controls_wrapper">
                <GoogleSignInButton {...googleSignInProps} />

                <EmailAddressField
                  disabled={isLoading || isSubmitting}
                  name={EMAIL_ADDRESS}
                  formikProps={formikProps}
                />
                <div className="button_wrapper">
                  <button
                    className="primary_button"
                    disabled={isLoading || isSubmitting}
                    type="submit"
                  >
                    {strings.APPLY_NOW}
                  </button>
                </div>
              </div>
              <p className={statusMessage.type === 'error' ? 'email_note_error' : 'email_note_warning'} aria-live="polite">
                {statusMessage?.message}
              </p>
            </form>
          );
        }}
      </Formik>
      <p className="email_first_tos_label_gray">
        {strings.CREATING_ACCOUNT}
        <a href="/terms" target="_blank">{strings.TERMS_OF_USE}</a>
        {strings.ACKNOWLEDGE}
        <a href="/privacy" target="_blank">{strings.PRIVACY_POLICY}</a>
        {strings.AGREE_TO_RECEIVE_EMAIL_ALERTS}
      </p>
    </section>
  );
}
